import type { HomePageData } from "../services/HomeService";
import { isPlaceholderMealName } from "./resolveMealDisplayName";

export interface HomeGlucoseSpikeMeal {
  meal_id: string;
  meal_type: string;
  meal_time: string;
  meal_time_display?: string;
  title?: string;
  this_meal_spike_mg_dl?: number;
  previous_meal_remaining_mg_dl?: number;
  is_cumulative?: boolean;
  spike_category?: string;
  spike_band?: string;
  glucose_spike?: Record<string, unknown> | null;
  [key: string]: unknown;
}

export interface ActiveMealDetail {
  meal_id?: string;
  meal_type?: string;
  meal_name?: string;
  meal_title?: string;
  meal_time?: string;
  date?: string;
  meal_items?: Array<Record<string, unknown>>;
  food_items?: Array<Record<string, unknown>>;
  meal_totals?: Record<string, unknown>;
  totals?: Record<string, unknown>;
  glucose_spike?: Record<string, unknown> | null;
  product_name?: string;
  barcode?: string;
  [key: string]: unknown;
}

export function getLatestGlucoseSpikeMeal(
  homeData: HomePageData | undefined
): HomeGlucoseSpikeMeal | undefined {
  const meals = homeData?.["Glucose Spikes"]?.meals ?? [];
  if (!meals.length) return undefined;
  return [...meals].sort(
    (a, b) => new Date(b.meal_time).getTime() - new Date(a.meal_time).getTime()
  )[0] as HomeGlucoseSpikeMeal;
}

export function extractMealsArrayFromResponse(response: unknown): unknown[] {
  const actualData =
    (response as { data?: unknown })?.data !== undefined
      ? (response as { data: unknown }).data
      : response;

  if (!actualData || typeof actualData !== "object") return [];

  const data = actualData as Record<string, unknown>;
  if (Array.isArray(data.meal_data)) return data.meal_data;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.meals)) return data.meals;
  if (Array.isArray(actualData)) return actualData as unknown[];
  return [];
}

export function normalizeMealRecord(meal: Record<string, unknown>): ActiveMealDetail {
  let foodItems = (meal.food_items as ActiveMealDetail["food_items"]) ?? [];
  const mealName =
    (meal.meal_name as string) ||
    (meal.product_name as string) ||
    (meal.food_name as string) ||
    (meal.item_name as string) ||
    (meal.generic_name as string) ||
    "";

  if ((!foodItems || foodItems.length === 0) && (mealName || meal.barcode)) {
    foodItems = [
      {
        name: mealName || String(meal.barcode ?? "Unknown Item"),
        product_name: mealName || String(meal.barcode ?? "Unknown Item"),
        source: "barcode",
        calorie_content_kcal: Number(meal.calories) || 0,
        protein_content_g: Number(meal.protein) || 0,
        carbohydrate_content_g: Number(meal.carbohydrates ?? meal.carbs) || 0,
        fat_content_g: Number(meal.fat) || 0,
        sugar_g: Number(meal.sugar) || 0,
        dietary_fiber_g: Number(meal.fiber) || 0,
        saturated_fat_g: Number(meal.saturated_fat) || 0,
        GI_estimate: Number(meal.gi_estimate) || 0,
        brand: meal.brand,
        barcode: meal.barcode,
      },
    ];
  } else if (foodItems?.length) {
    foodItems = foodItems.map((item) => ({
      ...item,
      name:
        item.name ||
        item.product_name ||
        item.food_name ||
        item.item_name ||
        mealName ||
        "Unknown Item",
    }));
  }

  const totals =
    (meal.totals as ActiveMealDetail["totals"]) ||
    (meal.meal_totals as ActiveMealDetail["meal_totals"]) ||
    undefined;

  const apiMealName = typeof meal.meal_name === "string" ? meal.meal_name.trim() : "";
  const productName =
    typeof meal.product_name === "string" ? meal.product_name.trim() : "";
  const firstItemName =
    typeof foodItems?.[0]?.name === "string"
      ? foodItems[0].name.trim()
      : typeof foodItems?.[0]?.product_name === "string"
        ? foodItems[0].product_name.trim()
        : "";

  const resolvedMealName =
    (apiMealName && !isPlaceholderMealName(apiMealName) && apiMealName) ||
    (productName && !isPlaceholderMealName(productName) && productName) ||
    (mealName && !isPlaceholderMealName(mealName) && mealName) ||
    (firstItemName && !isPlaceholderMealName(firstItemName) && firstItemName) ||
    apiMealName ||
    productName ||
    mealName ||
    undefined;

  return {
    ...meal,
    meal_id: (meal.meal_id as string) || (meal.id as string),
    meal_name: resolvedMealName,
    product_name: productName || undefined,
    meal_type: meal.meal_type as string | undefined,
    meal_time: (meal.meal_time as string) || (meal.date as string),
    meal_items: foodItems,
    food_items: foodItems,
    meal_totals: totals,
    totals,
    glucose_spike: (meal.glucose_spike as Record<string, unknown> | null) ?? null,
  };
}

export function getLatestMealByTime(
  meals: ActiveMealDetail[]
): ActiveMealDetail | undefined {
  if (!meals.length) return undefined;
  return [...meals].sort((a, b) => {
    const aTime = new Date(String(a.meal_time || a.date || 0)).getTime();
    const bTime = new Date(String(b.meal_time || b.date || 0)).getTime();
    return bTime - aTime;
  })[0];
}

export function findMealById(
  meals: ActiveMealDetail[],
  mealId: string | null | undefined
): ActiveMealDetail | undefined {
  if (!mealId) return undefined;
  return meals.find((meal) => meal.meal_id === mealId || meal.id === mealId);
}

export function buildGlucoseSpikeFromHomeMeal(
  meal: HomeGlucoseSpikeMeal | undefined
): Record<string, unknown> | undefined {
  if (!meal) return undefined;
  if (meal.glucose_spike && typeof meal.glucose_spike === "object") {
    return meal.glucose_spike;
  }
  if (meal.this_meal_spike_mg_dl == null) return undefined;
  return {
    this_meal_spike_mg_dl: meal.this_meal_spike_mg_dl,
    previous_meal_remaining_mg_dl: meal.previous_meal_remaining_mg_dl ?? 0,
    is_cumulative: meal.is_cumulative ?? false,
    spike_category: meal.spike_category,
    spike_band: meal.spike_band,
    title: meal.title,
  };
}

/** Glucose spike for the active (latest) meal — not day-level cumulative unless it matches. */
export function resolveActiveMealGlucoseSpike(
  homeData: HomePageData | undefined,
  latestHomeSpikeMeal: HomeGlucoseSpikeMeal | undefined,
  activeMealDetail: ActiveMealDetail | undefined
): Record<string, unknown> | undefined {
  const detailSpike = activeMealDetail?.glucose_spike;
  if (detailSpike && typeof detailSpike === "object") {
    return detailSpike;
  }

  const homeSpike = homeData?.glucose_spike;
  const totalMeals =
    homeData?.["Glucose Spikes"]?.total_meals_count ??
    homeData?.["Glucose Spikes"]?.meals?.length ??
    0;

  if (homeSpike && totalMeals <= 1) {
    return homeSpike as Record<string, unknown>;
  }

  if (homeSpike && !homeSpike.is_cumulative && totalMeals > 0) {
    return homeSpike as Record<string, unknown>;
  }

  return buildGlucoseSpikeFromHomeMeal(latestHomeSpikeMeal);
}
